import { isRouteErrorResponse, useRouteError } from "react-router-dom";
import { useCallback, useMemo, useRef } from "react";
import { motion } from "framer-motion";
import { Canvas, useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { useAppSelector } from "@/redux/store";
import { selectTheme } from "@/redux/slices/uiSlice";
import Logo from "@/components/shared/logo/Logo";

/* ─── Resolve "auto" to actual dark/light using the DOM attribute ─────────── */
function isDarkResolved(theme: "light" | "dark" | "auto"): boolean {
  if (theme === "dark") return true;
  if (theme === "light") return false;
  // "auto" → read what ThemeProvider actually set on <html>
  if (typeof document !== "undefined") {
    return document.documentElement.dataset.theme === "dark";
  }
  return false;
}

/* ─── Per-theme color palettes for Three.js (can't use CSS vars in JS) ───── */
const PALETTE = {
  dark: { base: "#c0caff", acc: "#5C6FFF", em: "#ff6b6b", glyph: "#ff6b6b", fog: "#0e0f13" },
  light: { base: "#8890b8", acc: "#5C6FFF", em: "#ff3131", glyph: "#ff3131", fog: "#fdfcff" },
} as const;

/* ─── Module-level Three.js reusables (avoid GC in useFrame) ─────────────── */
const _ev = new THREE.Vector3();
const _emw = new THREE.Vector3();

/* ─── Spiral-galaxy particle targets ─────────────────────────────────────── */
function buildTargets(N: number): Float32Array {
  const t = new Float32Array(N * 3);
  for (let i = 0; i < N; i++) {
    const i3 = i * 3;
    const a = (i / N) * Math.PI * 20;
    const r = (i / N) * 5.5 + Math.random() * 0.7;
    t[i3] = Math.cos(a) * r;
    t[i3 + 1] = (Math.random() - 0.5) * 3 + Math.sin(a * 0.25) * 0.9;
    t[i3 + 2] = Math.sin(a) * r;
  }
  return t;
}

/* ─── Particle field ──────────────────────────────────────────────────────── */
function ErrorParticles({
  mouseRef,
  isDark,
}: {
  mouseRef: React.MutableRefObject<{ x: number; y: number }>;
  isDark: boolean;
}) {
  const N = useMemo(
    () => (typeof window !== "undefined" && window.innerWidth < 640 ? 700 : 1600),
    []
  );
  const ptRef = useRef<THREE.Points>(null);
  const velRef = useRef(new Float32Array(N * 3));
  const tgtRef = useRef(buildTargets(N));

  // Start exploded/scattered so particles visibly coalesce into the spiral
  const posArr = useMemo(() => {
    const p = new Float32Array(N * 3);
    for (let i = 0; i < N; i++) {
      p[i * 3] = (Math.random() - 0.5) * 22;
      p[i * 3 + 1] = (Math.random() - 0.5) * 22;
      p[i * 3 + 2] = (Math.random() - 0.5) * 22;
    }
    return p;
  }, [N]);

  const colArr = useMemo(() => {
    const pal = isDark ? PALETTE.dark : PALETTE.light;
    const c = new Float32Array(N * 3);
    const ca = new THREE.Color(pal.acc);
    const ce = new THREE.Color(pal.em);
    const cw = new THREE.Color(pal.base);
    for (let i = 0; i < N; i++) {
      const r = Math.random();
      const col = r > 0.96 ? ce : r > 0.78 ? ca : cw;
      c[i * 3] = col.r;
      c[i * 3 + 1] = col.g;
      c[i * 3 + 2] = col.b;
    }
    return c;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [N, isDark]);

  useFrame(({ clock, camera }) => {
    const mesh = ptRef.current;
    if (!mesh) return;
    const pos = mesh.geometry.attributes.position.array as Float32Array;
    const vel = velRef.current;
    const tgt = tgtRef.current;
    const { x: mx, y: my } = mouseRef.current;

    _ev.set(mx, my, 0.5).unproject(camera);
    _ev.sub(camera.position).normalize();
    const dz = _ev.z !== 0 ? -camera.position.z / _ev.z : 0;
    _emw.copy(camera.position).addScaledVector(_ev, dz);

    const t = clock.elapsedTime;
    for (let i = 0; i < N; i++) {
      const i3 = i * 3;

      vel[i3] += (tgt[i3] - pos[i3]) * 0.038;
      vel[i3 + 1] += (tgt[i3 + 1] - pos[i3 + 1]) * 0.038;
      vel[i3 + 2] += (tgt[i3 + 2] - pos[i3 + 2]) * 0.038;

      // Slow galaxy drift
      vel[i3] += -pos[i3 + 2] * 0.0007;
      vel[i3 + 2] += pos[i3] * 0.0007;

      // Mouse repulsion
      const dx = pos[i3] - _emw.x;
      const dy = pos[i3 + 1] - _emw.y;
      const dz2 = pos[i3 + 2] - _emw.z;
      const d2 = dx * dx + dy * dy + dz2 * dz2;
      if (d2 < 5 && d2 > 0.001) {
        const f = 0.4 / d2;
        vel[i3] += dx * f;
        vel[i3 + 1] += dy * f;
        vel[i3 + 2] += dz2 * f;
      }

      vel[i3 + 1] += Math.sin(t * 0.45 + i * 0.004) * 0.00022;

      vel[i3] *= 0.89;
      vel[i3 + 1] *= 0.89;
      vel[i3 + 2] *= 0.89;

      pos[i3] += vel[i3];
      pos[i3 + 1] += vel[i3 + 1];
      pos[i3 + 2] += vel[i3 + 2];
    }

    mesh.geometry.attributes.position.needsUpdate = true;
    mesh.rotation.y = t * 0.06;
  });

  return (
    <points ref={ptRef}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" args={[posArr, 3]} />
        <bufferAttribute attach="attributes-color" args={[colArr, 3]} />
      </bufferGeometry>
      <pointsMaterial
        size={0.034}
        vertexColors
        sizeAttenuation
        transparent
        opacity={isDark ? 0.82 : 0.9}
        depthWrite={false}
      />
    </points>
  );
}

/* ─── Tumbling wireframe icosahedron — the "broken" glyph ────────────────── */
function BrokenGlyph({ isDark }: { isDark: boolean }) {
  const meshRef = useRef<THREE.Mesh>(null);
  const pal = isDark ? PALETTE.dark : PALETTE.light;

  useFrame(({ clock }) => {
    if (!meshRef.current) return;
    const t = clock.elapsedTime;
    meshRef.current.rotation.x = t * 0.18;
    meshRef.current.rotation.y = t * 0.27;
    meshRef.current.rotation.z = t * 0.09;
  });

  return (
    <mesh ref={meshRef}>
      <icosahedronGeometry args={[1.3, 1]} />
      <meshBasicMaterial
        color={pal.glyph}
        wireframe
        transparent
        opacity={isDark ? 0.13 : 0.22}
      />
    </mesh>
  );
}

/* ─── Error Boundary ──────────────────────────────────────────────────────── */
export default function ErrorBoundary() {
  // Redux Provider wraps RouterProvider in main.tsx so this is safe
  const theme = useAppSelector(selectTheme);
  const isDark = isDarkResolved(theme);
  const pal = isDark ? PALETTE.dark : PALETTE.light;

  const error = useRouteError();
  const mouseRef = useRef({ x: 0, y: -100 });

  let code = "500";
  let title = "Something went wrong.";
  let subtitle = "An unexpected error occurred on our end. Please try again.";

  if (isRouteErrorResponse(error)) {
    code = String(error.status);
    if (error.status === 404) {
      title = "Page not found.";
      subtitle = "The page you're looking for doesn't exist or has been moved.";
    } else if (error.status === 403) {
      title = "Access denied.";
      subtitle = "You don't have permission to view this page.";
    } else if (error.status === 401) {
      title = "Not authenticated.";
      subtitle = "Please sign in to continue.";
    }
  }

  const onMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    mouseRef.current.x = ((e.clientX - r.left) / r.width) * 2 - 1;
    mouseRef.current.y = -((e.clientY - r.top) / r.height) * 2 + 1;
  }, []);

  const onMouseLeave = useCallback(() => {
    mouseRef.current = { x: 0, y: -100 };
  }, []);

  return (
    <div
      className="relative min-h-dvh overflow-hidden bg-bg text-text"
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
    >
      {/* Ambient gradient blobs */}
      <div className="pointer-events-none absolute inset-0 z-0">
        <div
          className="absolute rounded-full"
          style={{
            top: -220,
            left: -180,
            width: 700,
            height: 700,
            background: `radial-gradient(circle, ${isDark ? "rgba(255,107,107,0.13)" : "rgba(255,49,49,0.07)"} 0%, transparent 65%)`,
          }}
        />
        <div
          className="absolute rounded-full"
          style={{
            bottom: -200,
            right: -200,
            width: 600,
            height: 600,
            background: `radial-gradient(circle, ${isDark ? "rgba(92,111,255,0.09)" : "rgba(92,111,255,0.06)"} 0%, transparent 65%)`,
          }}
        />
      </div>

      {/* Three.js canvas */}
      <Canvas
        camera={{ position: [0, 0, 10], fov: 50 }}
        className="absolute! inset-0 z-0"
        style={{ width: "100%", height: "100%" }}
      >
        <fog attach="fog" args={[pal.fog, 18, 32]} />
        <ambientLight intensity={0.4} />
        <ErrorParticles mouseRef={mouseRef} isDark={isDark} />
        <BrokenGlyph isDark={isDark} />
      </Canvas>

      {/* Content layer */}
      <div className="relative z-10 flex min-h-dvh flex-col">
        {/* Header */}
        <div className="p-5 pb-0 pl-8">
          <Logo />
        </div>

        {/* Center content */}
        <div className="flex flex-1 items-center justify-center px-6">
          <div className="w-full max-w-[460px] text-center">
            {/* Frosted glass panel */}
            <div
              className="rounded-2xl border border-border px-8 py-10"
              style={{
                background: isDark ? "rgba(14,15,19,0.6)" : "rgba(253,252,255,0.75)",
                backdropFilter: "blur(16px)",
                WebkitBackdropFilter: "blur(16px)",
              }}
            >
              {/* Error code */}
              <motion.div
                initial={{ opacity: 0, y: -24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
                className="select-none font-black leading-none tracking-tight text-bad"
                style={{
                  fontSize: "clamp(68px, 13vw, 120px)",
                  letterSpacing: "-0.04em",
                  marginBottom: 12,
                  textShadow: isDark
                    ? "0 0 60px rgba(255,49,49,0.3)"
                    : "0 4px 24px rgba(255,49,49,0.18)",
                }}
              >
                {code}
              </motion.div>

              {/* Divider */}
              <motion.div
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ delay: 0.22, duration: 0.45, ease: "easeOut" }}
                className="mx-auto mb-5"
                style={{
                  height: 1,
                  maxWidth: 160,
                  background: isDark
                    ? "linear-gradient(90deg, transparent, rgba(255,49,49,0.35), transparent)"
                    : "linear-gradient(90deg, transparent, rgba(255,49,49,0.25), transparent)",
                }}
              />

              {/* Title */}
              <motion.h1
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.45 }}
                className="mb-2 text-xl font-bold tracking-tight text-text"
              >
                {title}
              </motion.h1>

              {/* Subtitle */}
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.42, duration: 0.45 }}
                className="mb-7 text-sm leading-relaxed text-gray-txt"
              >
                {subtitle}
              </motion.p>

              {/* Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.52, duration: 0.4 }}
                className="flex flex-wrap justify-center gap-3"
              >
                <button
                  onClick={() => (window.location.href = "/")}
                  className="rounded-lg border border-border bg-light px-5 py-2.5 text-sm font-semibold text-text transition-all hover:border-pri/40 hover:bg-light-2 active:scale-95"
                >
                  ← Go home
                </button>

                <button
                  onClick={() => window.location.reload()}
                  className="rounded-lg border border-border bg-transparent px-5 py-2.5 text-sm font-semibold text-gray-txt transition-all hover:bg-light hover:text-text active:scale-95"
                >
                  Try again
                </button>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Footer hint */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.85 }}
          className="pb-5 text-center text-[11px] text-gray-700"
        >
          Hover to interact with particles
        </motion.p>
      </div>
    </div>
  );
}
